import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { College } from '../../../../../../../data/commission/models/college.model';
import { Cahier } from '../../../../../../../data/habilitation/models/cahier.model';
import { forkJoin } from 'rxjs';
import { SessionContext } from '../../../../../../../core/services/config/app.settings';
import { CahiersService } from '../../../../../../../data/commission/services/cahiers/cahiers.service';
import { CollegeService } from '../../../../../../../data/commission/services/college/college.service';
import { OrganismesService } from '../../../../../../../data/intervenant/services/organismes/organismes.service';
import { Operateur } from '../../../../../../../data/intervenant/models/operateur.model';
import { OperateursService } from '../../../../../../../data/intervenant/services/operateurs/operateurs.service';
import { ComQualif } from '../../../../../../../data/commission/models/com-qualif.model';
import { JuresService } from '../../../../../../../data/commission/services/jures/jures.service';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

export function ngbDateRequired(control: AbstractControl): ValidationErrors | null {
  const value: NgbDateStruct = control.value;

  if (!value) {
    return { required: true };
  }

  if (!value.year || !value.month || !value.day) {
    return { required: true };
  }

  return null;
}

@Component({
    selector: 'app-add-edit-qualification',
    templateUrl: './add-edit-qualification.component.html',
    styleUrls: ['./add-edit-qualification.component.scss'],
    standalone: false
})
export class AddEditQualificationComponent implements OnInit {
    formQualification: UntypedFormGroup;
    comQualifAUpdate: ComQualif;
    pageTitle: string;
    qualification: ComQualif;
    idQualif: number;
    operateurs: Operateur[];
    operateurSearch: Operateur;
    refColleges: College[];
    cdcs: Cahier[];
    idOrganisme: number;
    idJure: number;
    idCdc: number;
    cahiersPossedes: Cahier[];

    get cdcsCtrl() {
        return this.formQualification.get('cdcs');
    }

    get college() {
        return this.formQualification.get('college');
    }

    get operateur() {
        return this.formQualification.get('operateur');
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly modal: NgbActiveModal,
        private readonly cahierService: CahiersService,
        private readonly collegeService: CollegeService,
        private readonly operateurService: OperateursService,
        private readonly jureService: JuresService,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.idOrganisme = SessionContext.get('idOrganisme');
        if (this.idQualif) {
            this.jureService.getQualificationById(this.idQualif).subscribe(
                data => {
                    // this.initForm();
                    this.translate.get('page.admin.jure.qualifications.titleUpdate').subscribe((res: string) => {
                        this.pageTitle = res;
                    });
                    this.idCdc = data.idCahier;
                    this.loadData(data);
                },
                error => console.error('Erreur lors du chargement de la qualification', error)
            );
        } else {
            // Cas d'ajout d'une nouvelle qualification
            // this.initForm();
            this.translate.get('page.admin.jure.qualifications.titleAdd').subscribe((res: string) => {
                this.pageTitle = res;
            });
            this.loadData();
        }
    }

    private loadData(qualif?: ComQualif): void {
        forkJoin(
            this.cahierService.getCahiers(),
            this.collegeService.getColleges()
        ).subscribe(([cahiers, colleges]) => {
            this.cdcs = this.idQualif ? cahiers : cahiers.filter(cahier => !this.cahiersPossedes.some(cahierPossede => cahierPossede.id === cahier.id));
            this.refColleges = colleges;
            if (qualif && qualif.operateurs.length > 0) {
                this.operateurSearch = qualif.operateurs[0];
            }

            if (qualif) {
                this.initForm(qualif);
            } else {
                this.initForm();
            }
        });
    }

    private initForm(qualif?: ComQualif) {
        const isEdit = !!qualif;

        this.formQualification = this.fb.group({
            college: [isEdit ? this.refColleges.find(c => c.id === qualif.colleges[0].id) : null, Validators.required],

            // multi CDC en ajout, 1 CDC en edit
            cdcs: [{
                value: isEdit ? [this.cdcs.find(c => c.id === qualif.idCahier)] : [],
                disabled: isEdit
            }, Validators.required],

            dateFormationIni: [
                isEdit ? this.toNgbDateStruct(qualif.dateFormationInit) : null,
                [Validators.required, ngbDateRequired]
            ],
            dateFormationDer: [isEdit && qualif.dateFormationDer ? this.toNgbDateStruct(qualif.dateFormationDer) : null],
            operateur: [(isEdit && qualif.idsOperateurs.length > 0) ? this.operateurSearch : null],
        });
    }

    private toNgbDateStruct(date: moment.Moment | string | null): NgbDateStruct | null {
        if (!date) {
            return null; // Retourne null si la date est null ou non définie
        }
        const momentDate = moment.isMoment(date) ? date : moment(date);
        return { year: momentDate.year(), month: momentDate.month() + 1, day: momentDate.date() };
    }

  onSubmit() {
     if (this.formQualification.invalid) {
        Object.keys(this.formQualification.controls).forEach(key => {
          this.formQualification.get(key).markAsTouched();
        });
        return;
      }

    const raw = this.formQualification.getRawValue();
    const selectedCdcs: Cahier[] = raw.cdcs || [];

    // Sécurité : en ajout, il faut au moins 1 CDC
    if (!this.idQualif && selectedCdcs.length === 0) return;

    // Mode EDIT : comportement existant (une seule maj)
    if (this.idQualif) {
      const comQualif = this.comQualifAUpdate;

      comQualif.idJure = Number(this.idJure);
      comQualif.idCahier = this.idCdc;
      comQualif.colleges = [raw.college];

      if (this.operateurSearch) comQualif.idsOperateurs = [this.operateurSearch.id];
      else comQualif.idsOperateurs = raw.operateur ? [raw.operateur.id] : [];

      comQualif.dateFormationInit = raw.dateFormationIni
        ? moment([raw.dateFormationIni.year, raw.dateFormationIni.month - 1, raw.dateFormationIni.day])
        : null;

      comQualif.dateFormationDer = raw.dateFormationDer
        ? moment([raw.dateFormationDer.year, raw.dateFormationDer.month - 1, raw.dateFormationDer.day])
        : null;

      this.jureService.updateQualifById(this.idQualif, comQualif).subscribe(
        () => this.modal.close('qualification_updated'),
        err => console.error('Error updating qualification', err)
      );
      return;
    }

    let  ComQualifs : ComQualif[] = [];
    // Mode AJOUT MULTI : N créations
    selectedCdcs.forEach((cdc: Cahier) => {
      const comQualif = new ComQualif();
      comQualif.idJure = Number(this.idJure);
      comQualif.idCahier = cdc.id;
      comQualif.colleges = [raw.college];

      const op = this.operateurSearch ? this.operateurSearch : raw.operateur;
      comQualif.idsOperateurs = op ? [op.id] : [];

      comQualif.dateFormationInit = raw.dateFormationIni
        ? moment([raw.dateFormationIni.year, raw.dateFormationIni.month - 1, raw.dateFormationIni.day])
        : null;

      comQualif.dateFormationDer = raw.dateFormationDer
        ? moment([raw.dateFormationDer.year, raw.dateFormationDer.month - 1, raw.dateFormationDer.day])
        : null;

        ComQualifs.push(comQualif);
      });

      this.jureService.addQualification(ComQualifs).subscribe(
        () => {
        this.modal.close({
                      action: 'qualification_added',
                      qualifications: ComQualifs
                    });
      },
        err => console.error('Error adding qualifications', err)
      );
  }

  onClose() {
    this.modal.close('cancel');
  }

    selectOperateur(newItem: Operateur) {
        this.formQualification.patchValue({ operateur: newItem });
        this.operateurSearch = newItem;
    }
}
